import {
  ApplicantStatus,
  ExecutionModes,
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { PremiumRentalHistoryAddress } from "types/landlord/reports";

interface Params {
  applicantStatus: ApplicantStatus | string;
  surveyVerification: Verification | undefined;
  /** Premium flow: per-address survey existence from detailed report */
  premiumReportAddresses?: PremiumRentalHistoryAddress[];
  /** Light flow: housing_history verification from applicant.verifications */
  housingHistoryVerification?: Verification;
}

export const checkIsSurveyManualPending = ({
  applicantStatus,
  surveyVerification,
  premiumReportAddresses,
  housingHistoryVerification
}: Params): boolean => {
  const applicantHasSubmitted =
    applicantStatus === ApplicantStatus.Submitted ||
    applicantStatus === ApplicantStatus.OnHold;

  if (!applicantHasSubmitted) return false;
  if (surveyVerification?.settings?.execution_mode !== ExecutionModes.Manual)
    return false;

  const status = surveyVerification?.status;
  const isPremium =
    surveyVerification?.name === VerificationNames.PremiumRentalHistory;

  if (status === VerificationStatus.Pending) return true;

  if (status === VerificationStatus.Processing) {
    if (isPremium) {
      if (!premiumReportAddresses) return false;
      return premiumReportAddresses.some(
        (addr) =>
          addr.self_attested_data.housing_type === "rent" &&
          Object.keys(addr.survey).length === 0
      );
    }
    if (housingHistoryVerification)
      return hasUnsentLightSurveys(housingHistoryVerification);
    return false;
  }

  return false;
};

const hasUnsentLightSurveys = (
  housingHistoryVerification: Verification
): boolean => {
  const addresses = (housingHistoryVerification.steps?.find(
    (s) => s.name === "address"
  )?.data?.address ?? []) as {
    housing_type?: string;
    rent?: { landlord_email?: string };
  }[];

  // Lite surveys are numbered by Rent address, landlord email or not.
  const rentIndexesWithEmail = addresses
    .filter((a) => a.housing_type === "Rent")
    .flatMap((a, rentIndex) => (a.rent?.landlord_email ? [rentIndex] : []));

  if (rentIndexesWithEmail.length === 0) return false;

  const sentSurveys = Object.values(
    housingHistoryVerification.landlord_has_sent_surveys ?? {}
  ).flat();

  return rentIndexesWithEmail.some(
    (rentIndex) =>
      !sentSurveys.some((s) => s.property_address_index === rentIndex)
  );
};
